// Eigentümer-Detail (Handoff Eigentümer §6.5): Kopfzeile, Widget-Werte und die deterministische
// KI-Zusammenfassung aus Fakten. Reine Funktionen – testbar.

import { eur } from '../immobilien/format';
import { deDatum } from '../immobilien/objektDetail';
import { anredeKurz, mietdauerKurz } from '../mieter/detail';
import { istVerwaltet, istWegUns, type RollenWerte, type Zuordnung } from './rollen';
import type { EigStatus, EigZeile, PortalZustand } from './liste';

/** Portal-Zustand aus profiles.status (wie in der Liste). */
/** Status kommt vom verknüpften Zugang (profile_id, #584) – ein verknüpfter Datensatz braucht keine eigene E-Mail. */
export function portalAus(status: string | null, _email: string): PortalZustand {
  return status === 'Aktiv' ? 'aktiv' : status === 'Inaktiv' ? 'gesperrt' : 'kein';
}

/** „FRAU · WEG · SEV · EIGENTÜMER SEIT 01.03.2021“ (Großschreibung macht die Darstellung). */
export function kicker(p: { anrede: string | null | undefined; firma: boolean; rollen: readonly string[]; status: EigStatus; beginn: string | null; endetAm: string | null }): string {
  const anrede = p.firma ? 'Firma' : anredeKurz(p.anrede);
  const zeit = p.status === 'ehemalig' ? 'Ehemaliger Eigentümer'
    : p.status === 'uebernahme' && p.beginn ? `Übernahme am ${deDatum(p.beginn)}`
      : p.beginn ? `Eigentümer seit ${deDatum(p.beginn)}` : 'Eigentümer';
  return [anrede, ...p.rollen, zeit].filter(Boolean).join(' · ');
}

/** Laufzeit des Verwaltervertrags: „5 Jahre 7 Mon.“ bzw. „ab 01.12.2026“. */
export function vertragsDauer(beginn: string | null, heute: string): string {
  if (!beginn) return '–';
  return beginn > heute ? `ab ${deDatum(beginn)}` : mietdauerKurz(beginn, heute);
}

/** „Mietverwaltung“, „Sondereigentumsverwaltung“ oder beides. */
export function vertragsArt(w: Pick<RollenWerte, 'hasMV' | 'hasSEV'>): string {
  return [w.hasMV ? 'Mietverwaltung' : '', w.hasSEV ? 'Sondereigentumsverwaltung' : ''].filter(Boolean).join(' + ') || '–';
}

export interface PortfolioWerte {
  objekte: number;
  einheiten: number;
  flaeche: number;
  /** Belegung der verwalteten Einheiten in % (null = nichts verwaltet) */
  belegung: number | null;
  /** Summe MEA der WEG-Einheiten (in Tausendsteln, wie in den Daten) */
  mea: number;
  /** verwaltete Einheiten / davon vermietet */
  verwaltet: number;
  vermietet: number;
}

export function portfolioWerte(zuordnungen: readonly Zuordnung[]): PortfolioWerte {
  const verwaltete = zuordnungen.filter(z => istVerwaltet(z.rolle)).flatMap(z => z.einheiten);
  const vermietet = verwaltete.filter(e => e.vermietet).length;
  const weg = zuordnungen.filter(z => istWegUns(z.rolle)).flatMap(z => z.einheiten);
  return {
    objekte: new Set(zuordnungen.map(z => z.objektId)).size,
    einheiten: zuordnungen.reduce((s, z) => s + z.einheiten.length, 0),
    flaeche: Math.round(zuordnungen.reduce((s, z) => s + z.einheiten.reduce((t, e) => t + e.flaeche, 0), 0)),
    belegung: verwaltete.length ? Math.round((vermietet / verwaltete.length) * 100) : null,
    mea: Math.round(weg.reduce((s, e) => s + (e.mea ?? 0), 0) * 100) / 100,
    verwaltet: verwaltete.length,
    vermietet,
  };
}

/** „1 Objekt · 2 WE“ */
export function portfolioText(p: Pick<PortfolioWerte, 'objekte' | 'einheiten'>): string {
  return `${p.objekte} ${p.objekte === 1 ? 'Objekt' : 'Objekte'} · ${p.einheiten} WE`;
}

/** Zeile je Zuordnung für „Objekte & Rollen“: „Wohnpark Elsterblick · 3 EG“ + Rolle. */
export function zuordnungsZeilen(zuordnungen: readonly Zuordnung[]): { key: string; text: string; rolle: string; objektId: string }[] {
  return zuordnungen.flatMap(z => (z.ganzesObjekt || z.einheiten.length === 0
    ? [{ key: `${z.objektId}|${z.rolle}`, text: `${z.objektName} · ${z.ganzesObjekt ? 'ganzes Objekt' : 'ohne Einheiten'}`, rolle: z.rolle.replace('+', ' + '), objektId: z.objektId }]
    : z.einheiten.map(e => ({ key: `${z.objektId}|${z.rolle}|${e.id}`, text: `${z.objektName} · ${e.bezeichnung}`, rolle: z.rolle.replace('+', ' + '), objektId: z.objektId }))));
}

/** Monatliche Abrechnung: Miete − Hausgeld − Vergütung − Rücklage = Auszahlung (Balken in % der Miete). */
export function kontoTeile(w: Pick<RollenWerte, 'miete' | 'hgSev' | 'vergM' | 'instM' | 'ausz'>): { text: string; balken: { anteil: number; farbe: string }[] } {
  const teile = [
    `Miete ${eur(w.miete, { nachkomma: 0 })}`,
    w.hgSev ? `Hausgeld ${eur(w.hgSev, { nachkomma: 0 })}` : '',
    w.vergM ? `Vergütung ${eur(w.vergM, { nachkomma: 0 })}` : '',
    w.instM ? `Rücklage ${eur(w.instM, { nachkomma: 0 })}` : '',
  ].filter(Boolean);
  const basis = w.miete > 0 ? w.miete : 0;
  const balken = basis === 0 ? [] : ([[Math.max(0, w.ausz), 'var(--ok)'], [w.hgSev, 'var(--info)'], [w.vergM ?? 0, 'var(--warn)'], [w.instM, 'var(--bad)']] as const)
    .filter(([v]) => v > 0)
    .map(([v, farbe]) => ({ anteil: Math.round((v / basis) * 1000) / 10, farbe }));
  return { text: teile.join(' − '), balken };
}

/** Hausgeldkonto-Untertitel: Zahlweg SEPA und/oder Verrechnung aus der SEV-Miete. */
export function hausgeldText(w: Pick<RollenWerte, 'direktHg' | 'hgSevUns'>): string {
  if (w.direktHg > 0 && w.hgSevUns > 0) return `SEPA ${eur(w.direktHg, { nachkomma: 0 })} · ${eur(w.hgSevUns, { nachkomma: 0 })} aus SEV-Miete verrechnet`;
  if (w.hgSevUns > 0) return 'wird aus SEV-Mieteinnahmen verrechnet';
  return 'SEPA-Lastschrift';
}

/** IBAN maskiert: „•••• 3000“ */
export function ibanKurz(iban: string | null | undefined): string | null {
  const s = (iban ?? '').replace(/\s+/g, '');
  return s.length >= 4 ? `•••• ${s.slice(-4)}` : null;
}

export interface EigFakten {
  name: string;
  zeile: Pick<EigZeile, 'status' | 'beginn' | 'endetAm' | 'werte' | 'zuordnungen'>;
  heute: string;
  portal: PortalZustand;
  miteigentuemer: number;
  termine: number;
}

/** „Zusammenfassung“ ohne KI: deterministischer Text aus den Fakten. */
export function zusammenfassung(f: EigFakten): string {
  const w = f.zeile.werte;
  const p = portfolioWerte(f.zeile.zuordnungen);
  const s: string[] = [];
  if (p.objekte === 0) s.push(`${f.name} ist derzeit keinem Objekt zugeordnet.`);
  else if (f.zeile.status === 'uebernahme' && f.zeile.beginn) s.push(`${f.name} übernimmt am ${deDatum(f.zeile.beginn)} ${portfolioText(p)}.`);
  else s.push(`${f.name} besitzt ${portfolioText(p)}${f.zeile.beginn ? ` und ist seit ${mietdauerKurz(f.zeile.beginn, f.heute)} Eigentümer` : ''}.`);
  if (w.rollen.length) s.push(`Rolle: ${vertragsRollen(w)}.`);
  if (w.verwaltet && p.verwaltet) s.push(p.vermietet === p.verwaltet ? 'Alle verwalteten Einheiten sind vermietet.' : `${p.verwaltet - p.vermietet} von ${p.verwaltet} verwalteten Einheiten stehen leer.`);
  if (w.hasWEG && w.hgM > 0) s.push(`Das Hausgeld beträgt ${eur(w.hgM, { nachkomma: 0 })} im Monat.`);
  if (f.zeile.status === 'endet' && f.zeile.endetAm) s.push(`Die Zuordnung endet am ${deDatum(f.zeile.endetAm)}.`);
  if (f.miteigentuemer > 0) s.push(`${f.miteigentuemer} ${f.miteigentuemer === 1 ? 'Miteigentümer ist' : 'Miteigentümer sind'} hinterlegt.`);
  if (f.termine > 0) s.push(`${f.termine} ${f.termine === 1 ? 'Termin steht' : 'Termine stehen'} an.`);
  s.push(f.portal === 'aktiv' ? 'Kommunikation läuft über das Eigentümerportal.' : 'Ein Portalzugang ist noch nicht aktiv.');
  return s.join(' ');
}

function vertragsRollen(w: Pick<RollenWerte, 'hasMV' | 'hasWEG' | 'hasSEV'>): string {
  return [w.hasMV ? 'Mietverwaltung' : '', w.hasWEG ? 'WEG-Mitglied' : '', w.hasSEV ? 'Sondereigentumsverwaltung' : ''].filter(Boolean).join(', ');
}
